import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { AdminService } from '@app/services/admin.service';
import { CussService } from '@app/services/cuss.service';
import packageInfo from '../../../package.json';
import { v4 as uuidv4 } from 'uuid';
import { NewRelicService } from '@app/services/micro/new-relic.service';
import { NewRelicParmsInt } from '@app/interfaces/ga4.interface';
import { environment } from '@env/environment';
import { normalizeNumberOfDecimals } from '@app/utils/functions/decimals';
import {
  isNumberOfDecimalsHeaderInScope,
  NUMBER_OF_DECIMALS_HEADER,
} from '@app/security/decimals-header';
import { ErrorLoggerService } from '@app/services/error-logger.service';
import {
  ENCRYPTED_ENDPOINT_SENSITIVE_HEADERS,
  ENCRYPTED_REDACTION,
  findEncryptedEndpoint,
} from '@app/security/encrypted-endpoints';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  urlCheckin = 'checkIn';
  // flightstatus (IROPS) en el BFF: se le agregan los headers de addHeadersIropsMicro.
  baseIropsMicro = '/kiosk/checkin/flightstatus';
  endPointName = '';
  urlBaseMicros = [
    environment.urlApiMicro,
    environment.urlApiCheckin,
    environment.urlApiMyTrips,
    environment.urlApiCheckinBFF,
    environment.urlApiPaymentBFF,
    environment.urlApiAncillariesBFF,
    environment.urlApiMicroEligibility,
    environment.urlApiContactability,
    environment.urlApiContactabilityAuth,
    environment.urlApiSelfReaccom,
    environment.urlApiWeb,
    environment.urlAncillaries,
    environment.urlApiPayment,
  ];

  urlBaseMonolite = [environment.urlApi];

  /**
   * Microservicio local (dispositivos del kiosko) que no deben recibir los
   * parámetros de sesión del kiosko (timeOfRequest, pos, kioskId, id, etc.).
   */
  urlsLocalDevices = ['localhost:3000'];

  urlsGetMonolite = ['carts', 'boarding-passes', 'issueBagTag'];

  endPointMicroservices = ['docheckin', 'getBoardingPass'];

  constructor(
    private _adminService: AdminService,
    private _cussService: CussService,
    private _newRelicService: NewRelicService,
    private _errorLogger: ErrorLoggerService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const { url } = req;
    let paramsHttp;
    this._newRelicService.kioskID = this._cussService.KioskID;
    if (url.includes(this.baseIropsMicro)) {
      paramsHttp = this.addHeadersIropsMicro(req);
      next.handle(paramsHttp);
    } else if (url.includes(this.urlCheckin) && this._adminService.admin) {
      if (this.isExcludedFromCheckIn(url)) {
        paramsHttp = req;
      } else {
        paramsHttp = this.addParametersCheckIn(req);
      }

      next.handle(paramsHttp);
    } else {
      if (url.includes('/exchange/reassociation')) {
        paramsHttp = req;
      } else if (this.isExcludedFromCheckIn(url) || this.isLocalDevice(url)) {
        paramsHttp = req;
      } else {
        paramsHttp = this.addParameterIdKiosk(req);
      }

      next.handle(req.clone());
    }

    // CI-21979: agrega el header numberOfDecimalsBE únicamente cuando CMS entrega
    // un valor válido (0/1/2) Y la operación está en alcance (POST/PUT ancillaries
    // v2, POST seatmap v2, POST seat v3). Sin default: si la propiedad está
    // ausente o es inválida, no se agrega el header. Se aplica sobre el request
    // final (después de los demás clones), conservando params/body/context/headers
    // y sin mutar el request original.
    if (isNumberOfDecimalsHeaderInScope(req.method, url)) {
      const decimals = normalizeNumberOfDecimals(this._adminService?.admin?.numberOfDecimalsBE);
      if (decimals !== undefined) {
        paramsHttp = this.addNumberOfDecimalsHeader(paramsHttp, decimals);
      }
    }

    return next.handle(paramsHttp).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.sendMetricNewRelic(paramsHttp, event);
          }
        },
        error: (error) => {
          this.sendMetricNewRelic(paramsHttp, error);
          this.reportHttpError(paramsHttp, error);
        },
      })
    );
  }

  private isExcludedFromCheckIn(url: string): boolean {
    return this.urlBaseMicros.some((excludedUrl) => url.includes(excludedUrl));
  }

  private isLocalDevice(url: string): boolean {
    return this.urlsLocalDevices.some((localUrl) => url.includes(localUrl));
  }

  addParametersCheckIn(req: HttpRequest<any>) {
    const { store, TSA, DOCV, Location, currency, Payment, iropReaccommed } =
      this._adminService.admin;

    const timeOfRequest = new Date().getTime().toString();
    return req.clone({
      params: req.params
        .set('timeOfRequest', timeOfRequest)
        .set('pos', 'kiosk')
        .set('store', store ?? 'MX')
        .set('overrideTsa', !TSA)
        .set('overrideDocv', DOCV)
        .set('kioskId', this._cussService.KioskID)
        .set('uiVersion', packageInfo.version)
        .set('location', Location)
        .set('currency', currency ?? 'MXN')
        .set('paymentEnabled', Payment)
        .set('uiVersion', '4.5.19-109')
        .set('languageUI', 'es_MX')
        .set('language', 'ES')
        .set('uuid', uuidv4())
        .set('reaccommed', iropReaccommed),
    });
  }

  addParameterIdKiosk(req: HttpRequest<any>) {
    const timeOfRequest = new Date().getTime().toString();
    return req.clone({
      params: req.params
        .set('timeOfRequest', timeOfRequest)
        .set('pos', 'kiosk')
        .set('kioskId', this._cussService.KioskID)
        .set('id', this._cussService.KioskID),
    });
  }

  /**
   * CI-21979: clona el request agregando el header numberOfDecimalsBE con el
   * valor válido ya resuelto (0/1/2), enviado como string HTTP. `setHeaders`
   * conserva los headers, params, body y context existentes; no muta el original.
   */
  private addNumberOfDecimalsHeader(req: HttpRequest<any>, decimals: number): HttpRequest<any> {
    if (decimals === 0 || decimals === null) {
      return req;
    }

    return req.clone({
      setHeaders: { [NUMBER_OF_DECIMALS_HEADER]: String(decimals) },
    });
  }

  addHeadersIropsMicro(req): any {
    const cartId = this._adminService.admin.cartId;

    return req.clone({
      setHeaders: {
        accept: 'application/json',
        flow: 'CHECKIN',
        'x-transaction-id': cartId || '',
        channel: environment.channel,
        'Content-type': 'application/json',
      },
    });
  }

  /**
   * Reporta errores HTTP a New Relic via ErrorLoggerService.
   * Envía como PageAction (KIOSK_SSBD / KIOSK_UNHANDLED_ERROR) y como JS Error (noticeError).
   */
  private reportHttpError(request: HttpRequest<any>, error: HttpErrorResponse): void {
    try {
      const httpError = new Error(`HTTP ${error.status}: ${request.method} ${request.url}`);
      httpError.name = 'HttpErrorResponse';

      this._errorLogger.logError(httpError, 'HttpInterceptor', {
        httpUrl: request.url,
        httpStatus: error.status,
        httpMethod: request.method,
        httpStatusText: error.statusText || '',
      });
    } catch (e) {
      console.warn('[ApiInterceptor] Failed to report HTTP error', e);
    }
  }

  async sendMetricNewRelic(request: any, response: HttpResponse<any>) {
    let metricsNewRelic: NewRelicParmsInt;
    if (this.urlBaseMicros.find((url) => response.url?.includes(url))) {
      if (this.isEndPointMicroservices(request, response)) {
        await this.sendMetricNewRelic2(request, response);
      } else {
        metricsNewRelic = await this.getMetricsNewRelic(request, response);
        this._newRelicService.sendMetric(metricsNewRelic);
      }
    } else if (this.urlBaseMonolite.find((url) => response?.url?.includes(url))) {
      metricsNewRelic = await this.getMetricsNewRelic(request, response);
      this._newRelicService.sendMetricMonolite(metricsNewRelic);
    }
  }

  isEndPointMicroservices(request, response) {
    if (this.urlBaseMicros.find((url) => response?.url?.includes(url))) {
      let name = request?.url?.split('/')?.pop();
      name = this.urlsGetMonolite.find((endpoint) => request?.url?.includes(endpoint)) ?? name;
      name = name.includes('?') ? name.split('?')[0] : name;

      if (
        this.endPointMicroservices.find((endpoint) => endpoint === name) &&
        response.error === undefined
      ) {
        this.endPointName = name;

        return true;
      }
    }

    return false;
  }

  async getMetricsNewRelic(request, response) {
    const { method } = request;

    let paramsObject: Record<string, string>;
    if (method === 'GET' && request?.params?.keys().length > 0) {
      const { params } = request;
      paramsObject = {};
      for (const key of params.keys()) {
        paramsObject[key] = params.get(key) || '';
      }
    }

    const associatedRequest: any = {
      url: request?.url,
      method: request?.method,
      headers: request?.headers,
      body: request?.body,
      params: paramsObject,
    };
    const associatedResponse: any = {
      url: response?.url,
      status: response?.status,
      headers: response?.headers,
      body: response?.body || response?.error,
    };

    // Endpoints cifrados: Seguridad no permite enviar a New Relic la data en claro.
    // Los errores (en claro desde el backend) se conservan para diagnóstico.
    // Los endpoints con newRelicRedaction: false (docheckin, getBoardingPass, bagtag,
    // flightDetails, flightstatus, profile) siguen llegando en claro.
    const encryptedEndpoint = findEncryptedEndpoint(request?.method, request?.url);
    if (encryptedEndpoint && encryptedEndpoint.newRelicRedaction !== false) {
      this.redactEncryptedMetric(
        associatedRequest,
        associatedResponse,
        this.isError(response) === 'true'
      );
    }

    let name = associatedRequest?.url?.split('/')?.pop();
    name =
      this.urlsGetMonolite.find((endpoint) => associatedRequest?.url?.includes(endpoint)) ?? name;
    name = name.includes('?') ? name.split('?')[0] : name;
    if (name == '' && associatedRequest?.url?.includes(environment.urlApi)) {
      name = 'checkIn';
    }

    if (associatedRequest.url.includes('/exchanges/am-exchanges-api/pnr')) {
      name = 'pnrIrops';
    }
    const dataEndPoint = { associatedRequest, associatedResponse };
    const dataNewRelic: NewRelicParmsInt = {
      admin: this._adminService.getDataAdminInt(),
      enviroment: environment.production,
      microRequest: dataEndPoint.associatedRequest,
      microResponse: dataEndPoint.associatedResponse,
      url: dataEndPoint.associatedRequest.url,
      name,
      transactionId: uuidv4(),
      passengerId: '',
      isError: this.isError(response),
      route: '',
      bookedLegCollection: dataEndPoint.associatedResponse?.body?.bookedLegCollection,
    };

    if (name == 'pnr') {
      // Crear una copia del body sin bookedLegCollection para NewRelic
      const bodyWithoutBookedLeg = { ...dataEndPoint.associatedResponse?.body };
      delete bodyWithoutBookedLeg.bookedLegCollection;
      dataNewRelic.microResponse = {
        ...dataEndPoint.associatedResponse,
        body: bodyWithoutBookedLeg,
      };
    }
    return dataNewRelic;
  }

  /**
   * Sustituye bodies y params por ENCRYPTED_REDACTION y enmascara los headers
   * con datos del pasajero. Muta los objetos de métrica, nunca el request real.
   */
  private redactEncryptedMetric(
    associatedRequest: any,
    associatedResponse: any,
    isError: boolean
  ): void {
    associatedRequest.body =
      associatedRequest.body == null ? associatedRequest.body : ENCRYPTED_REDACTION;
    associatedRequest.params = associatedRequest.params
      ? ENCRYPTED_REDACTION
      : associatedRequest.params;

    let headers: HttpHeaders | undefined = associatedRequest.headers;
    ENCRYPTED_ENDPOINT_SENSITIVE_HEADERS.forEach((name) => {
      if (headers?.has(name)) {
        headers = headers.set(name, ENCRYPTED_REDACTION);
      }
    });
    associatedRequest.headers = headers;

    if (!isError) {
      associatedResponse.body = ENCRYPTED_REDACTION;
    }
  }

  isError(response) {
    if (response.error !== undefined) return 'true';
    else return 'false';
  }

  async sendMetricNewRelic2(request, response) {
    switch (this.endPointName) {
      case this.endPointMicroservices[0]:
        this.sendMetricsDoCheckin(request, response, this.endPointName);
        break;

      case this.endPointMicroservices[1]:
        this.sendMetricsBoardingPass(request, response, this.endPointName);
        break;

      default:
        break;
    }
  }

  sendMetricsDoCheckin(request, response, name) {
    let data: any;
    let bookedLeg: any = response.body.bookedLegCollection;
    let passengers: any = JSON.parse(JSON.stringify(response.body.passengers));
    const lightPassenger: any = this.deleteFieldFromJson(
      'travelDoc',
      JSON.parse(JSON.stringify(response.body.passengers))
    );

    for (const passenger of passengers) {
      const { passengerId, checkinStatusBySegment } = passenger;

      for (const segment of checkinStatusBySegment) {
        const { segmentCode, checkinStatus, onStandByList } = segment;

        data = this.getNewRelicParams(
          request,
          response,
          name,
          checkinStatus,
          passengerId,
          null,
          segmentCode,
          onStandByList
        );

        const responseForNewRelic = JSON.parse(JSON.stringify(response));
        this.deleteFieldFromJson('bookedLegCollection', responseForNewRelic);
        this.deleteFieldFromJson('passengers', responseForNewRelic);
        this.deleteFieldFromJson('barcodeImage', lightPassenger);
        this.deleteFieldFromJson('qrCodeImage', lightPassenger);
        this.deleteFieldFromJson('googleWalletPass', lightPassenger);
        this.deleteFieldFromJson('appleWalletPass', lightPassenger);

        data.microResponse = responseForNewRelic;
        data.bookedLegCollection = bookedLeg;
        data.passengers = this.truncate(JSON.stringify(lightPassenger), 4085);
        data.travelDoc = segment?.boardingPass?.travelDoc;

        this._newRelicService.sendMetric(data);
      }
    }

    if (data?.passengers === undefined) {
      data = this.getNewRelicParams(request, response, name, false, null, null, null, null);

      this._newRelicService.sendMetric(data);
    }
  }

  sendMetricsBoardingPass(request, response, name) {
    let data: any;
    let bookedLeg: any = response.body.bookedLegCollection;
    let passengers: any = JSON.parse(JSON.stringify(response.body.passengers));
    const lightPassenger: any = this.deleteFieldFromJson(
      'travelDoc',
      JSON.parse(JSON.stringify(response.body.passengers))
    );

    for (const passenger of passengers) {
      const { checkinStatusBySegment, passengerNameNumber } = passenger;

      for (const segment of checkinStatusBySegment) {
        const { segmentCode } = segment;
        const pectab =
          response?.body?.pectabList.length > 0 ? response?.body?.pectabList[0].pectab : null;

        data = this.getNewRelicParams(
          request,
          response,
          name,
          false,
          passengerNameNumber,
          pectab,
          segmentCode,
          false
        );

        data.bookedLegCollection = bookedLeg;
        data.passengers = this.truncate(JSON.stringify(lightPassenger), 4085);
        data.travelDoc = segment?.boardingPass?.travelDoc;

        const responseForNewRelic = JSON.parse(JSON.stringify(response));
        this.deleteFieldFromJson('bookedLegCollection', responseForNewRelic);
        this.deleteFieldFromJson('passengers', responseForNewRelic);
        this.deleteFieldFromJson('barcodeImage', lightPassenger);
        this.deleteFieldFromJson('qrCodeImage', lightPassenger);
        this.deleteFieldFromJson('googleWalletPass', lightPassenger);
        this.deleteFieldFromJson('appleWalletPass', lightPassenger);

        data.microResponse = responseForNewRelic;

        this._newRelicService.sendMetric(data);
      }
    }

    if (response?.body.pectabList === undefined) {
      data = this.getNewRelicParams(request, response, name, false, null, null, null, null);

      this._newRelicService.sendMetric(data);
    }
  }

  getNewRelicParams(
    request: HttpRequest<any>,
    response: HttpResponse<any>,
    name: string,
    checkinStatus: boolean,
    paxId: string,
    pectab: string,
    segmentCode: string,
    onStandByList: boolean
  ) {
    const dataNewRelic: NewRelicParmsInt = {
      admin: this._adminService.getDataAdminInt(),
      enviroment: environment.production,
      microRequest: request,
      microResponse: response,
      url: request.url,
      name,
      transactionId: uuidv4(),
      passengerId: paxId,
      isError: this.getIsError(checkinStatus, onStandByList, pectab),
      route: segmentCode,
    };

    return dataNewRelic;
  }

  getIsError(checkinStatus: boolean, onStandByList: boolean, pectab: string): string {
    return checkinStatus || onStandByList || pectab ? 'false' : 'true';
  }

  deleteFieldFromJson(fieldName: string, response: any) {
    if (typeof response === 'object' && response !== null) {
      for (let key in response) {
        if (key === fieldName) {
          delete response[key];
        } else if (typeof response[key] === 'object') {
          this.deleteFieldFromJson(fieldName, response[key]);
        }
      }
    }

    return response;
  }

  truncate(objeto: any, longitudMaxima: number): any {
    if (typeof objeto === 'string') {
      return objeto.length > longitudMaxima ? objeto.substring(0, longitudMaxima) + '...' : objeto;
    }

    return objeto;
  }
}
